import { ContentChunkFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import Button from '@/ui/components/shared/Button';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { getOpenerCheckIDFromCookie } from '@/ui/components/tiles/OpenerCompatibilityCheck';
import { OpenerCheckModelNotFoundSubmissionType } from '@/ui/components/tiles/OpenerCompatibilityCheck/types/OpenerCheckResultsSubmission';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import { IntercomFormData } from './types/IntercomFormData';

export default function NotFoundScreen({
  chunk,
  afterSubmit,
}: {
  chunk: ContentChunkFragment;
  afterSubmit: () => void;
}) {
  const validationSchema = yup.object().shape({
    brand: yup.string().required('Brand is required'),
    model: yup.string().optional(),
    email: yup.string().email('Invalid email').required('Email is required'),
  });

  const nootFoundScreenChunk = chunk.chunks?.[0];
  const title = extractComponentFromChunk(nootFoundScreenChunk, 'title', 'SingleLineContent')?.text;
  const description = extractComponentFromChunk(
    nootFoundScreenChunk,
    'description',
    'RichTextContent',
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IntercomFormData>({
    resolver: yupResolver(validationSchema),
  });
  const { language } = useLocalization();

  const syncOpenerCheckModelNotFoundToDb = async (
    openerCheckResults: OpenerCheckModelNotFoundSubmissionType,
  ): Promise<void> => {
    try {
      const response = await axios.post(
        apiPaths.opener.storeModelNotFoundResults,
        openerCheckResults,
      );
    } catch (e) {
      console.error('Error saving opener check when model was not found:', e);
    }
  };

  // TODO: Implement storing the form data
  const onSubmit = async (data: any) => {
    const toBeSubmitted = {
      intercomModel: data.model,
      brandName: data.brand,
      language,
      email: data.email,
    };

    try {
      // send opener check results to the backend, subsequently to the database
      const openerCheckMetaInfo = getOpenerCheckIDFromCookie();
      if (openerCheckMetaInfo !== undefined) {
        await syncOpenerCheckModelNotFoundToDb({
          openerCheckId: openerCheckMetaInfo.openerCheckId,
          newOpenerCheck: openerCheckMetaInfo.newOpenerCheck,
          customerData: toBeSubmitted,
        });
      }
    } catch (error) {
      console.error('Error during form submission:', error);
    } finally {
      afterSubmit();
    }
  };

  return (
    <div>
      <h2 className="mb-2 text-2xl font-bold">{title}</h2>
      <RichTextRenderer blocks={description} />
      <form onSubmit={handleSubmit(onSubmit)} className="mx-auto mt-6 max-w-lg space-y-2">
        <TextInput
          id="brand"
          srLabel="Brand"
          register={register}
          errors={errors.brand}
          placeholder="Intercom brand"
        />
        <TextInput
          id="model"
          srLabel="Model"
          register={register}
          errors={errors.model}
          placeholder="Intercom model"
        />
        <TextInput
          id="email"
          srLabel="Email"
          register={register}
          errors={errors.email}
          placeholder="Email"
        />
        <Button type="submit" className="mx-auto w-full">
          Submit
        </Button>
      </form>
    </div>
  );
}
